import { z } from 'zod';
import { ClientProjectActivityStatusSchema, ClientProjectActivityTypeSchema } from './client.js';
// =============================================================================
// Dashboard
// =============================================================================
export const DashboardTotalsResponseSchema = z.object({
    wipTotalMinorUnits: z.int(),
    wipActivityCount: z.int(),
    wipCurrency: z.string(),
    // On an accounting-enabled account this is the Accounts Receivable
    // (Debtors Control) closing balance, not the sum of open invoice rows: it
    // nets off payments, credit notes, unallocated client credits, and manual
    // AR journals, so it can legitimately be negative (clients net in credit).
    // On an accounting-disabled account it stays the gross sum of open
    // (APPROVED + SENT) invoice totals. The two eras deliberately diverge from
    // each other and from `outstandingInvoiceCount` below.
    outstandingTotalMinorUnits: z.int(),
    // The number of open (APPROVED + SENT) invoices, in both eras. This is
    // status-based while the total above is balance-based, so they can
    // legitimately disagree: a partially-paid invoice still counts here but
    // has already reduced the total, and an unallocated client credit reduces
    // the total without closing any invoice.
    outstandingInvoiceCount: z.int(),
    // The ledger's base currency on an accounting-enabled account, 'ZAR'
    // otherwise.
    outstandingCurrency: z.string(),
});
// =============================================================================
// Activities Report Preview
// =============================================================================
export const ActivitiesReportPreviewRowSchema = z.object({
    date: z.string(),
    matterReference: z.string(),
    matterName: z.string(),
    clientName: z.string(),
    performedBy: z.string(),
    description: z.string(),
    // Raw enum value from the activity. Empty string covers activities with no
    // type recorded (legacy rows). Labels are resolved renderer-side via
    // getTypeLabel / getActivityStatusLabel in @packages/types.
    // eslint-disable-next-line zod-compat/no-zod-empty-string-literal -- response-only schema; never exposed as a tool body
    type: z.union([ClientProjectActivityTypeSchema, z.literal('')]),
    units: z.number(),
    ratePerUnitMinorUnits: z.int(),
    currency: z.string(),
    amountMinorUnits: z.int(),
    // eslint-disable-next-line zod-compat/no-zod-empty-string-literal -- response-only schema; never exposed as a tool body
    status: z.union([ClientProjectActivityStatusSchema, z.literal('')]),
});
export const ActivitiesReportPreviewResponseSchema = z.object({
    rows: z.array(ActivitiesReportPreviewRowSchema),
    totalCount: z.int(),
});
// =============================================================================
// Activities Report Column Keys
// =============================================================================
export const ACTIVITIES_REPORT_COLUMN_KEYS = [
    'date',
    'matterReference',
    'matterName',
    'clientName',
    'status',
    'performedBy',
    'description',
    'type',
    'units',
    'rate',
    'amount',
];
export const ActivitiesReportColumnKeySchema = z.enum(ACTIVITIES_REPORT_COLUMN_KEYS);
